import { enumMembers, type ApiSchema } from "../apiSchema";
import { readBoolean, readNullableString, readObject, readOneOf, readString, readInteger } from "../http/jsonDecoding";
import { readDatasetVersionSummary, type DatasetVersionSummary } from "./knowledgeDataDecoders";

/**
 * Releases and ResearchSpace publishing as the server sends them
 * (`server/src/dmw/api/routes/data/release_bodies.py`, knowledge-data.md K-12, K-13).
 */

export type PublicationState = ApiSchema<"PublicationState">;
export type RuntimeStatus = ApiSchema<"RuntimeStatus">;

export const PUBLICATION_STATES = enumMembers<PublicationState>({
  requested: true,
  exporting: true,
  configuring: true,
  applying: true,
  published: true,
  failed: true,
});
const RUNTIME_STATUSES = enumMembers<RuntimeStatus>({ running: true, stopped: true, unreachable: true });

/** One release put into ResearchSpace: exported, configured, applied. */
export interface Publication {
  readonly publicationId: string;
  readonly datasetId: string;
  readonly version: number;
  readonly state: PublicationState;
  readonly requestedAt: string;
  readonly finishedAt: string | null;
  readonly problem: { readonly code: string; readonly detail: string } | null;
}

/** A release of the project: a dataset version, with its newest publication. */
export interface ReleaseSummary {
  readonly datasetId: string;
  readonly title: string;
  readonly version: DatasetVersionSummary;
  readonly researchSpace: Publication | null;
  /** ResearchSpace shows this release for the project now. */
  readonly liveInResearchSpace: boolean;
}

export interface ResearchSpaceProject {
  /** The installation has a ResearchSpace. */
  readonly available: boolean;
  readonly publishes: boolean;
  /** The publication ResearchSpace shows for the project, while it publishes. */
  readonly live: Publication | null;
}

export interface ResearchSpaceStatus {
  readonly available: boolean;
  readonly status: RuntimeStatus | null;
}

export function readPublication(value: unknown): Publication {
  const body = readObject(value, "publication");
  const problem = body["problem"] == null ? null : readObject(body["problem"], "publication problem");
  return {
    publicationId: readString(body, "publication_id"),
    datasetId: readString(body, "dataset_id"),
    version: readInteger(body, "version"),
    state: readOneOf(body, "state", PUBLICATION_STATES),
    requestedAt: readString(body, "requested_at"),
    finishedAt: readNullableString(body, "finished_at"),
    problem: problem === null ? null : { code: readString(problem, "code"), detail: readString(problem, "detail") },
  };
}

export function readReleaseSummary(value: unknown): ReleaseSummary {
  const body = readObject(value, "release");
  return {
    datasetId: readString(body, "dataset_id"),
    title: readString(body, "title"),
    version: readDatasetVersionSummary(body["version"]),
    researchSpace: body["researchspace"] == null ? null : readPublication(body["researchspace"]),
    liveInResearchSpace: readBoolean(body, "live_in_researchspace"),
  };
}

export function readResearchSpaceProject(value: unknown): ResearchSpaceProject {
  const body = readObject(value, "ResearchSpace setting");
  return {
    available: readBoolean(body, "available"),
    publishes: readBoolean(body, "publishes"),
    live: body["live"] == null ? null : readPublication(body["live"]),
  };
}

export function readResearchSpaceStatus(value: unknown): ResearchSpaceStatus {
  const body = readObject(value, "ResearchSpace status");
  return {
    available: readBoolean(body, "available"),
    status: body["status"] == null ? null : readOneOf(body, "status", RUNTIME_STATUSES),
  };
}
